import {
    Assessment,
    AssessmentStatus,
    Issue,
    Location
} from "../../../API";
import {
    AutoSizer,
    CellMeasurer,
    CellMeasurerCache,
    List,
    ListRowRenderer,
    WindowScroller
} from "react-virtualized";
import {
    Box,
    Theme,
    useMediaQuery
} from "@mui/material";
import React, {
    useMemo
} from "react";
import {
    isAfter,
    isSameDay,
    startOfDay,
    startOfToday,
    startOfWeek,
    subMonths
} from "date-fns";

import AddIcon from '@mui/icons-material/Add';
import AddLocationAltIcon from '@mui/icons-material/AddLocationAlt';
import { AppliedFilterChips } from "../AppliedFilterChips";
import { AssessmentFilterStatus } from "../../../lib/dashboard/types/IssueFilter";
import { CreateIssueButton } from "../issue/CreateIssueButton";
import { CreateLocationButton } from "../CreateLocationButton";
import { EmotionJSX } from "@emotion/react/types/jsx-namespace";
import { ImageEntity } from "../../../lib/dashboard/types/ImageEntity";
import { IssueCard } from "../IssueCard";
import { IssueStatus } from "../../../models";
import { ListIssuesFilter } from "../filter/ListIssuesFilter";
import LocationCardHeader from "../LocationCardHeader";
import { MAX_MONTHS_BACK } from "../../../lib/dashboard/constants/DateConstants";
import { NoIssuesOrAssessmentsMessage } from "../NoIssuesOrAssessmentsMessage";
import { SuccessfulAssessmentCard } from "../SuccessfulAssessmentCard";
import { ViewSwitcher } from "../ViewSwitcher";
import { useAssessments } from "../contexts/AssessmentsContextProvider";
import { useIssueFilter } from "../contexts/IssueFilterContext";
import { useIssues } from "../contexts/IssuesContextProvider";
import { useLocations } from "../contexts/LocationContextProvider";
import { useTags } from "../contexts/TagsContextProvider";

type ListItem =
    { type: "location"; location: Location; }
    | { type: "issue"; resource: Issue; }
    | { type: "assessment"; resource: Assessment; };

export const ListIssuesPage = () => {
    const { issues, images, linkRegistries, onEditIssueSubmit } = useIssues();
    const { locations } = useLocations();
    const { assessments } = useAssessments();
    const printComponentRef = React.useRef(null);
    const { filter, updateFilter } = useIssueFilter();
    const { tags } = useTags();
    const { createLocation } = useLocations();
    const { submitIssue } = useIssues();
    const isScreenSmallerThanSm = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

    const isIssue = (resource: Issue | Assessment): resource is Issue => {
        return "locationId" in resource;
    };

    const isAssessment = (resource: Issue | Assessment): resource is Assessment => {
        return "resourceId" in resource;
    };

    const getResourceLocationId = (resource: Issue | Assessment): string | undefined => {
        if (isIssue(resource) && resource.locationId) {
            return resource.locationId;
        }
        if (isAssessment(resource) && resource.resourceId) {
            return resource.resourceId;
        }
        return undefined;
    };

    const locationIdToLocationNameMap = useMemo<Map<string, string>>(() => {
        return new Map(locations.map((location) => [location.id, location.name || `Location-${location.id}`]));
    }, [locations]);

    const filteredIssues = useMemo(() => {
        const filtered = issues
            .filter(() => filter.assessmentId ? false : true)
            .filter((issue) => filter.issueId ? issue.id === filter.issueId : true)
            .filter((issue) => issue.locationId && filter.locationIds ? filter.locationIds.includes(issue.locationId) : true)
            .filter((issue) => {
                if (!filter.date?.start || !filter.date?.end) {
                    return true;
                }
                const issueDate = startOfDay(new Date(issue.createdAt));
                return isSameDay(issueDate, filter.date.start) || isSameDay(issueDate, filter.date.end) || isAfter(issueDate, filter.date.start) && isAfter(filter.date.end, issueDate);
            })
            .filter((issue) => {
                const issueFilters: Set<IssueStatus> = new Set((filter.statuses || [])
                    .filter((status) => Object.values(IssueStatus).includes(status as IssueStatus))
                    .map((status) => status as IssueStatus)
                );
                const assessmentFilters: Set<AssessmentFilterStatus> = new Set((filter.statuses || [])
                    .filter((status) => Object.values(AssessmentFilterStatus).includes(status as AssessmentFilterStatus))
                    .map((status) => status as AssessmentFilterStatus)
                );
                // If there are any issue filters, return true if the issue has the status
                if (issueFilters.size > 0) {
                    return issueFilters.has(issue.status as IssueStatus);
                }
                // If there is only one assessment filter and it is passed, return false
                if (assessmentFilters.size === 1 && assessmentFilters.has(AssessmentFilterStatus.PASSED)) {
                    return false;
                }
                return true;
            })
            .filter((issue) => filter.tagIds ? filter.tagIds.every((tagId) => issue.tagIds?.includes(tagId)) : true);
        return filtered;
    }, [issues, assessments, filter]);

    const filteredAssessments = useMemo(() => {
        return assessments
            .filter(() => filter.issueId ? false : true)
            .filter((assessment) => filter.assessmentId ? assessment.id === filter.assessmentId : true)
            .filter((assessment) => assessment.resourceId && filter.locationIds ? filter.locationIds.includes(assessment.resourceId) : true)
            .filter((assessment) => {
                const assessmentFilters: Set<AssessmentFilterStatus> = new Set((filter.statuses || [])
                    .filter((status) => Object.values(AssessmentFilterStatus).includes(status as AssessmentFilterStatus))
                    .map((status) => status as AssessmentFilterStatus)
                );
                if (assessmentFilters.size === 1 && assessmentFilters.has(AssessmentFilterStatus.FAILED)) {
                    return assessment.status === AssessmentStatus.ISSUES_FOUND;
                }
                return true;
            })
            .filter((assessment) => {
                if (!filter.date?.start || !filter.date?.end) {
                    return true;
                }
                const assessmentDate = startOfDay(new Date(assessment.createdAt));
                return isSameDay(assessmentDate, filter.date.start) || isSameDay(assessmentDate, filter.date.end) || isAfter(assessmentDate, filter.date.start) && isAfter(filter.date.end, assessmentDate);
            })
            .filter((assessment) => filter.tagIds ? filter.tagIds.every((tagId) => assessment.tagIds?.includes(tagId)) : true);
    }, [assessments, filter]);

    const locationIdToResourcesMap = useMemo<Map<string, Array<Issue | Assessment>>>(() => {
        return [
            ...issues,
            ...assessments.filter(assessment => assessment.status === AssessmentStatus.NO_ISSUES)
        ].reduce((acc, resource) => {
            const locationId = getResourceLocationId(resource);
            if (!locationId) {
                return acc;
            }
            if (!acc.has(locationId)) {
                acc.set(locationId, []);
            }
            acc.get(locationId)?.push(resource);
            return acc;
        }, new Map<string, Array<Issue | Assessment>>());
    }, [issues, assessments]);

    const locationIdToFilteredResourcesMap = useMemo<Map<string, Array<Issue | Assessment>>>(() => {
        return Array.from(locationIdToResourcesMap.entries()).reduce((acc, [locationId, resources]) => {
            const locationIssues = filteredIssues.filter(issue => issue.locationId === locationId);
            const locationAssessments = filteredAssessments.filter(assessment => assessment.resourceId === locationId);
            acc.set(locationId, [...locationIssues, ...locationAssessments]);
            return acc;
        }, new Map<string, Array<Issue | Assessment>>());
    }, [locationIdToResourcesMap, filteredIssues, filteredAssessments]);

    const locationsWithIssuesOrSuccessfulAssessment: Array<Location> = useMemo(() => {
        return Array.from(locationIdToFilteredResourcesMap.entries())
            .filter(([locationId, resources]) => resources.length > 0)
            .map(([locationId]) => locations.find((location) => location.id === locationId))
            .filter((location): location is Location => location !== undefined)
            .sort((location1, location2) => {
                const locationName1 = (location1?.name || '').toLowerCase();
                const locationName2 = (location2?.name || '').toLowerCase();
                return locationName1.localeCompare(locationName2);
            });
    }, [locations, locationIdToFilteredResourcesMap]);

    const earliestPossibleDate = useMemo<Date>(() => {
        let earliestDate = startOfWeek(subMonths(startOfToday(), MAX_MONTHS_BACK));
        if (issues.length === 0) {
            return earliestDate;
        }
        const firstIssueEpoch = issues.reduce((acc, issue) => {
            if (!issue.clientCreationDate) {
                return acc;
            }
            return Math.min(acc, issue.clientCreationDate);
        }, Number.MAX_SAFE_INTEGER);
        const firstIssueDate = startOfDay(new Date(firstIssueEpoch));
        const startOfWeekFirstIssue = startOfWeek(firstIssueDate);
        return new Date(Math.min(earliestDate.getTime(), startOfWeekFirstIssue.getTime()));
    }, [issues]);

    const issueIdToIssueNameMap = useMemo<Map<string, string>>(() => {
        return new Map(issues.map((issue) => [issue.id, issue.name || `Issue-${issue.id}`]));
    }, [issues]);

    const itemMeasurerCache = new CellMeasurerCache({
        fixedWidth: true,
        minHeight: 0
    });

    const parentIdToImagesMap = useMemo<Map<string, Array<ImageEntity>>>(() => {
        return images.reduce((acc, image) => {
            if (!image.parentId) {
                return acc;
            }
            if (!acc.has(image.parentId)) {
                acc.set(image.parentId, []);
            }
            acc.get(image.parentId)?.push(image);
            return acc;
        }, new Map<string, Array<ImageEntity>>());
    }, [images]);

    const locationIdToImagesUnderLocationMap = useMemo<Map<string, Array<ImageEntity>>>(() => {
        return locations.reduce((acc, location) => {
            const imagesUnderLocation = parentIdToImagesMap.get(location.id) || [];
            const filteredResourcesUnderLocation = locationIdToFilteredResourcesMap.get(location.id) || [];
            const imagesUnderResources = filteredResourcesUnderLocation.reduce((acc, resource) => {
                const resourceImages = parentIdToImagesMap.get(resource.id) || [];
                return [...acc, ...resourceImages];
            }, [] as Array<ImageEntity>);
            acc.set(location.id, [...imagesUnderLocation, ...imagesUnderResources]);
            return acc;
        }, new Map<string, Array<ImageEntity>>());
    }, [locations, parentIdToImagesMap, locationIdToFilteredResourcesMap]);

    const flattenedResourcesWithLocationHeader = useMemo(() => {
        return locationsWithIssuesOrSuccessfulAssessment.reduce((acc, location) => {
            acc.push({
                type: "location",
                location: location
            });
            acc.push(...(locationIdToFilteredResourcesMap.get(location.id) || []).map((resource) => {
                if (isIssue(resource)) {
                    return {
                        type: "issue" as const,
                        resource: resource
                    };
                }
                return {
                    type: "assessment" as const,
                    resource: resource
                };
            }));
            return acc;
        }, [] as Array<ListItem>);
    }, [locationsWithIssuesOrSuccessfulAssessment, locationIdToFilteredResourcesMap]);

    const renderItem: ListRowRenderer = ({ index, style, key, parent }): EmotionJSX.Element => {
        const item = flattenedResourcesWithLocationHeader[index];
        const resourceImages = item.type !== "location" ? parentIdToImagesMap.get(item.resource.id) || [] : [];
        const imagesUnderLocation = item.type === "location" ? locationIdToImagesUnderLocationMap.get(item.location.id) || [] : [];

        return (
            <CellMeasurer
                key={key}
                parent={parent}
                rowIndex={index}
                columnIndex={0}
                cache={itemMeasurerCache}
            >
                <Box style={style}>
                    <Box
                        style={{
                            pageBreakAfter: 'always',
                            breakAfter: 'page',
                            margin: 0,
                        }}
                    >
                        {item.type === "location" ? (
                            <LocationCardHeader location={item.location} images={imagesUnderLocation} />
                        ) : item.type === "issue" ? (
                            <IssueCard
                                issue={item.resource}
                                images={resourceImages}
                                inFocus={false}
                                onEditIssueSubmit={onEditIssueSubmit}
                            />
                        ) : (
                            <SuccessfulAssessmentCard
                                assessment={item.resource}
                                images={resourceImages}
                                inFocus={false}
                            />
                        )}
                    </Box>
                </Box>
            </CellMeasurer>
        );
    };

    return (
        <Box display="flex" flexDirection="column" gap={1}>
            <Box display="flex" flexDirection="column" gap={1}>
                <Box display="flex" flexDirection={{ xs: "row", md: "column" }} gap={2} flexWrap="wrap" justifyContent="space-between">
                    <Box display="flex" justifyContent="flex-end" gap={1} flexWrap="wrap">
                        <Box mr="auto">
                            <ListIssuesFilter
                                filter={filter}
                                setFilter={updateFilter}
                                locationOptions={locations}
                                earliestDate={earliestPossibleDate}
                                tagOptions={tags}
                            />
                        </Box>
                        <Box display="flex" gap={1} flexWrap="wrap" ml="auto">
                            {createLocation && (
                                <CreateLocationButton
                                    createLocation={createLocation}
                                    icon={<AddLocationAltIcon />}
                                    buttonProps={{
                                        color: "inherit",
                                        variant: "outlined",
                                        sx: { textTransform: "none", alignSelf: "flex-end" },
                                    }}
                                    text={isScreenSmallerThanSm ? "Location" : "Add Location"}
                                />
                            )}
                            {submitIssue &&
                                <CreateIssueButton
                                    icon={<AddIcon />}
                                    buttonProps={{
                                        color: "inherit",
                                        variant: "outlined",
                                        sx: { textTransform: "none", alignSelf: "flex-end" },
                                    }}
                                    text={isScreenSmallerThanSm ? "Issue" : "Add Issue"}
                                />
                            }
                        </Box>
                        <ViewSwitcher />
                    </Box>
                </Box>
                <Box>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                        <AppliedFilterChips
                            issueIdToIssueNameMap={issueIdToIssueNameMap}
                            locationIdToLocationNameMap={locationIdToLocationNameMap}
                        />
                    </Box>
                </Box>
            </Box>
            <Box ref={printComponentRef} bgcolor="background.paper" border={theme => `1px solid ${theme.palette.divider}`}>
                {locationsWithIssuesOrSuccessfulAssessment.length === 0 ? (
                    <NoIssuesOrAssessmentsMessage
                        propertyLinkRegistries={linkRegistries}
                        propertyIssues={issues}
                        propertyAssessments={assessments}
                        locationIdsFilter={filter.locationIds ?? []}
                        setFilter={updateFilter}
                    />
                ) : (
                    <AutoSizer
                        disableHeight
                    >
                        {({ width }) => {
                            return (
                                <WindowScroller
                                    scrollElement={document.getElementById("main-content") as Element || undefined}
                                >
                                    {({ height, scrollTop }) => {
                                        return (
                                            <>
                                                <List
                                                    autoHeight
                                                    height={height}
                                                    width={width}
                                                    scrollTop={scrollTop}
                                                    rowHeight={itemMeasurerCache.rowHeight}
                                                    rowRenderer={renderItem}
                                                    rowCount={flattenedResourcesWithLocationHeader.length}
                                                />
                                            </>
                                        );
                                    }}
                                </WindowScroller>
                            );
                        }}
                    </AutoSizer>
                )}
            </Box>
        </Box>
    );
};